/**
 * Layer 2's other half of searching: a place the trip does **not** have yet, found through the
 * server's Google endpoints (`GET place-search`, `GET place-search/:placeId`).
 *
 * The trip's own places filter as you type, because that list is already in hand and costs
 * nothing. This half runs on a button: every call here is a billed call at Google, so one press is
 * one search. Nothing is written from here either — a place the member picks goes in through the
 * create path the trip already has, with the editor pre-filled from `placeDetails`.
 */
import { PLACE_SEARCH_UNCONFIGURED } from "@trip/contracts/messages";
import { foldName, metresBetween, type PoiWindow } from "@trip/domain/poi";
import { ApiError, get } from "./api.ts";

/**
 * What Google says about whether a place is trading, where it says anything. The search never
 * returns `CLOSED_PERMANENTLY` (the server leaves those out); the details of one place can, and
 * then there is nothing to add.
 */
export type PlaceStatus = "CLOSED_PERMANENTLY" | "CLOSED_TEMPORARILY" | "FUTURE_OPENING";

/** One result of a search. `distanceM` only with a centre, `status` only where there is one. */
export type PlaceHit = {
  placeId: string;
  name: string;
  lat: number;
  lon: number;
  /** Whole metres from the centre that was sent. */
  distanceM?: number;
  status?: PlaceStatus;
};

/** One place read by id, with its opening hours as this project's windows (`[]` says nothing). */
export type FoundPlace = {
  placeId: string;
  name: string;
  lat: number;
  lon: number;
  windows: PoiWindow[];
  status?: PlaceStatus;
};

/** The shortest query the endpoint takes: a billed call on one letter finds nothing worth paying for. */
export const MIN_QUERY = 2;
/** The longest it takes. The field itself stops there, so a refusable search cannot be offered. */
export const MAX_QUERY = 200;
/**
 * How close two places of one name have to be to be the same place.
 *
 * Both ends of this matter. Google's position for a place is rarely the position the trip holds:
 * a place added by hand sits wherever the map was centred when the form was saved, and an imported
 * one sits where the list's own pin was — tens of metres apart for the same door. But a name is
 * not unique in a city either: a chain has a branch in each neighbourhood, and two unrelated
 * taquerías share a saint's name. 150 m is wide enough to cover the first and far narrower than
 * the second: it is one block, so the other branch is never folded into this one.
 */
export const SAME_PLACE_M = 150;
/**
 * The decimals a coordinate is sent with. The endpoint's pattern is a plain decimal of at most ten
 * places in at most sixteen characters, and MapLibre's own `getCenter` gives the full double
 * (-99.13320000000002), which that pattern refuses. Six places is about 11 cm — nothing against a
 * bias of twenty kilometres.
 */
const COORD_DECIMALS = 6;

/** The query the endpoint would take, or null: trimmed, and long enough to be worth a call. */
export function searchable(query: string): string | null {
  const q = query.trim();
  return q.length >= MIN_QUERY && q.length <= MAX_QUERY ? q : null;
}

/**
 * The centre, as the query string carries it: both or neither, never half of one, which the
 * endpoint refuses rather than biasing to Greenwich. The longitude is wrapped and the latitude
 * clamped, because a map panned twice round the world reports a longitude past 180 that is a
 * perfectly good place and would otherwise be a refusal.
 */
export function centreQuery(centre: [number, number] | null): string {
  if (centre === null) return "";
  const [lon, lat] = centre;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return "";
  const wrapped = ((((lon + 180) % 360) + 360) % 360) - 180;
  const clamped = Math.max(-90, Math.min(90, lat));
  return `&lat=${clamped.toFixed(COORD_DECIMALS)}&lon=${wrapped.toFixed(COORD_DECIMALS)}`;
}

/** One search, biased to where the map is looking when it knows. At most ten results. */
export const searchPlaces = (tripId: string, query: string, centre: [number, number] | null) =>
  get<{ items: PlaceHit[] }>(
    `/v1/trips/${tripId}/place-search?q=${encodeURIComponent(query)}${centreQuery(centre)}`,
  ).then((r) => (Array.isArray(r.items) ? r.items : []));

/** One place's name, position and hours, read once: when a member taps Add. */
export const placeDetails = (tripId: string, placeId: string) =>
  get<FoundPlace>(`/v1/trips/${tripId}/place-search/${encodeURIComponent(placeId)}`);

/**
 * Whether the trip already has this place, so it is not offered as something to add: one name by
 * `foldName` — the single folding rule the sync check and both search fields read — and within
 * `SAME_PLACE_M`. A coordinate that cannot be measured gives NaN, which is not within anything, so
 * an unmeasurable place is offered rather than silently dropped.
 */
export const onTripAlready = (
  place: { name: string; lat: number; lon: number },
  pois: { name: string; lat: number; lon: number }[],
) => {
  const name = foldName(place.name);
  return pois.some(
    (p) =>
      foldName(p.name) === name &&
      metresBetween(p.lat, p.lon, place.lat, place.lon) <= SAME_PLACE_M,
  );
};

/** How far a result is, in words. Never "from you": the centre sent was the map's, not a fix. */
export const distanceSaid = (metres: number) =>
  !Number.isFinite(metres)
    ? null
    : metres < 1000
      ? `${Math.round(metres)} m from the map centre`
      : `${(metres / 1000).toFixed(1)} km from the map centre`;

/** What a flagged result is marked with. A status with no words here is no flag at all. */
export const STATUS_SAID: Record<PlaceStatus, string> = {
  CLOSED_PERMANENTLY: "Closed down",
  CLOSED_TEMPORARILY: "Temporarily closed",
  FUTURE_OPENING: "Not open yet",
};

/** What the editor is not opened for: there is nothing to plan around a place that has shut down. */
export const CLOSED_DOWN =
  "Google says this place has closed down for good, so it is not worth adding.";
/** Said when the server has no key. Not a failure: this half simply is not switched on here. */
export const NOT_SET_UP =
  "Searching Google is not set up on this server, so only the places already on this trip can be found.";
/** Said when the budget for searches ran out. Named, so it does not read as a search that found nothing. */
export const TOO_OFTEN = "Google has been searched too many times just now. Try again in a minute.";
/** Said when the request never got an answer at all. Nothing is queued for later. */
export const SEARCH_OFFLINE = "Searching needs the network. Connect and try again.";

/**
 * A refused search in words, and whether that refusal is the server having no key — which is a
 * state of this server, not a search that went wrong, and is said as such. Every other refusal
 * keeps the server's own words, which are already written for a reader.
 */
export function searchFailed(e: unknown): { text: string; unconfigured: boolean } {
  if (e instanceof ApiError) {
    if (e.status === 503 && e.message === PLACE_SEARCH_UNCONFIGURED)
      return { text: NOT_SET_UP, unconfigured: true };
    if (e.status === 429) return { text: TOO_OFTEN, unconfigured: false };
    return { text: e.message, unconfigured: false };
  }
  return { text: SEARCH_OFFLINE, unconfigured: false };
}
